"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState } from "react";
import { Calendar as CalendarIcon, Loader } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function BookingForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [timeSlot, setTimeSlot] = useState<string>("");
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSubmit();
    if (!submitError) {
      toast.success("Booking request received", {
        description: "Damien's team will confirm availability within 4 hours."
      });
    }
  };
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-card border border-border rounded-md">
        <h3 className="font-default text-2xl md:text-3xl text-foreground mb-3 uppercase font-black">Confirmed.</h3>
        <p className="text-muted-foreground mb-6 font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Schedule another event
        </Button>
      </div>;
  }
  return <Form onSubmit={handleFormSubmit} className="space-y-7">
      <h3 className="font-default text-3xl md:text-[32px] text-foreground tracking-tighter uppercase font-black">
        Secure the stage
      </h3>

      <FormField control={control} name="name" rules={{
      required: "Full name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-steel-grey">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Enter your name" {...field} className="border-0 border-b border-steel-grey bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary font-default text-base h-11" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-steel-grey">
              Professional Email
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="email@organization.com" {...field} className="border-0 border-b border-steel-grey bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary font-default text-base h-11" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        required: "Phone is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-steel-grey">
                Direct Line
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (000) 000-0000" {...field} className="border-0 border-b border-steel-grey bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary font-default text-base h-11" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-steel-grey">
                Organization
              </FormLabel>
              <FormControl>
                <Input placeholder="Company Name" {...field} className="border-0 border-b border-steel-grey bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary font-default text-base h-11" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="station_type" rules={{
      required: "Please select an event type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-steel-grey">
              Engagement Type
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="w-full h-12 rounded-md bg-background border-border font-default text-base px-5">
                  <SelectValue placeholder="Select keynote or workshop" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="rounded-md bg-popover border-border">
                <SelectItem value="Keynote: Resilience Mindset">Keynote: Resilience Mindset</SelectItem>
                <SelectItem value="Keynote: Leading with Empathy">Keynote: Leading with Empathy</SelectItem>
                <SelectItem value="Executive Coaching Session">Executive Coaching Session</SelectItem>
                <SelectItem value="Leadership Workshop">Leadership Workshop</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="workshop_date" rules={{
      required: "Please select a date"
    }} render={({
      field
    }) => <FormItem className="flex flex-col">
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-steel-grey">
              Event Date
            </FormLabel>
            <Popover>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button type="button" variant="outline" className="w-full h-12 rounded-md bg-background border-border font-default text-base px-5 justify-between text-left font-normal">
                    {field.value ? new Date(field.value).toLocaleDateString("en-US", {
                weekday: "short",
                month: "long",
                day: "numeric",
                year: "numeric"
              }) : <span className="text-muted-foreground">Select preferred date</span>}
                    <SafeHydrate><CalendarIcon className="size-4 text-primary" /></SafeHydrate>
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 rounded-md border-border" align="start">
                <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => field.onChange(date?.toISOString() ?? "")} disabled={date => date < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus /></SafeHydrate>
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>} />

      <FormItem>
        <FormLabel className="font-mono text-xs uppercase tracking-widest text-steel-grey">
          Preferred Timezone/Slot
        </FormLabel>
        <RadioGroup value={timeSlot} onValueChange={setTimeSlot} className="flex flex-wrap gap-2">
          {["Morning", "Afternoon", "Evening", "Full Day"].map((slot, i) => <label key={slot} data-index={i} htmlFor={`slot-${slot}`} className={`cursor-pointer rounded-md border px-5 py-2 font-mono text-sm transition-all ${timeSlot === slot ? "bg-primary text-primary-foreground border-primary" : "bg-background text-foreground border-border hover:border-primary"}`}>
              <RadioGroupItem value={slot} id={`slot-${slot}`} className="sr-only" />
              {slot}
            </label>)}
        </RadioGroup>
      </FormItem>

      <FormField control={control} name="additional_requirements" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-steel-grey">
              Event Objectives
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your audience and the impact you want to create." {...field} className="border-0 border-b border-steel-grey bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary font-default text-base min-h-[80px] resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-md">
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}

      <div className="pt-4 space-y-3">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-md text-base font-black uppercase bg-primary hover:bg-ash-white text-primary-foreground transition-colors duration-300">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Processing...
            </> : <>Check Availability →</>}
        </Button>
        <p className="font-mono text-[10px] text-steel-grey text-center tracking-widest uppercase">
          Direct response within 4 business hours.
        </p>
      </div>
    </Form>;
}
export default function SecureYourDate() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const benefits = ["Customized Keynote Content", "Interactive Audience Q&A", "Post-Event Leadership Toolkit"];
  return <section data-section-id="5141" id="secure-your-date" className="bg-background text-foreground pt-32 md:pt-40 pb-24 md:pb-32 px-6 md:px-10 overflow-hidden">
      <Toaster position="top-right" richColors />
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-5 lg:col-start-1 transition-all duration-1000 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-primary mb-8">
              Booking · Availability
            </p>
            <h2 className="font-default text-5xl md:text-6xl lg:text-7xl leading-[0.9] text-foreground tracking-tighter mb-8 uppercase font-black">
              Secure your{" "}
              <span className="italic font-light text-primary">date</span>.
            </h2>
            <p className="text-base md:text-[17px] text-steel-grey leading-relaxed mb-10 max-w-md font-default">
              Bring high-octane leadership insights to your next event. Select your engagement type, 
              propose a date, and Damien's team will coordinate the logistics.
            </p>

            <div className="bg-secondary rounded-md p-6 md:p-7 mb-10 max-w-md border-l-4 border-primary shadow-lg">
              <ul className="space-y-3">
                {benefits.map((benefit, i) => <li key={benefit} data-index={i} className="flex items-center gap-3 font-default text-base text-foreground font-bold">
                    <span className="inline-block size-1.5 bg-primary shrink-0" />
                    {benefit}
                  </li>)}
              </ul>
            </div>

            <div className="flex items-center gap-5">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/damien_cole_stage_action.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/damien_cole_stage_action.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/damien_cole_stage_action.webp-wvc-sizes" alt="Damien Cole speaking on stage" loading="lazy" decoding="async" className="size-16 rounded-md object-cover shrink-0 border border-white/10" />
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-steel-grey mb-1">
                  Booking Status
                </p>
                <p className="font-default text-sm text-foreground font-bold">Now Booking · Q3 &amp; Q4 2026</p>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-6 lg:col-start-7 transition-all duration-1000 delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider formKey="secure_date_form_damien_cole" formId="secure_date_form_damien_cole" sectionName="secure-your-date" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company: "",
            workshop_date: "",
            station_type: "",
            additional_requirements: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            workshop_date: {
              required: true
            },
            station_type: {
              required: true
            }
          }} submitText="Check Availability" successMessage="Your booking request has been received. Damien's team will contact you within 4 hours." errorMessage="Something went wrong. Please try again or contact us directly." className="bg-card border border-border rounded-md p-8 md:p-12 shadow-xl">
              <BookingForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}